import React, { ComponentPropsWithoutRef } from "react"
import { useField, UseFieldConfig } from "react-final-form"
import { useId } from "@reach/auto-id"

export interface LabeledTextFieldProps extends ComponentPropsWithoutRef<"input"> {
  /** Field name. */
  name: string
  /** Field label. */
  label: string
  outerProps?: ComponentPropsWithoutRef<"div">
  fieldProps?: UseFieldConfig<string>
  items: Array<{ label: string | React.ReactNode; value: string }>
}

export const CompactRadioGroupField = React.forwardRef<HTMLInputElement, LabeledTextFieldProps>(
  ({ name, label, outerProps, fieldProps, className, items, ...props }, ref) => {
    const {
      input,
      meta: { touched, error, submitError, submitting },
    } = useField(name, {
      ...fieldProps,
    })
    const id = useId() + "name"

    const normalizedError = Array.isArray(error) ? error.join(", ") : error || submitError
    const showError = touched && normalizedError

    return (
      <div {...outerProps}>
        <fieldset>
          <legend className="sr-only">{label}</legend>
          <ul className="relative bg-white rounded-md -space-y-px">
            {items.map((item, index) => {
              let wrapperClasses = "relative border  p-4 flex flex-col md:pl-4 md:pr-6"
              if (index === 0) {
                wrapperClasses += " rounded-tl-md rounded-tr-md"
              }
              if (index === items.length - 1) {
                wrapperClasses += " rounded-bl-md rounded-br-md"
              }
              if (input.value === item.value) {
                wrapperClasses += " bg-secondary-50 border-secondary-200 z-10"
              } else {
                wrapperClasses += " border-gray-200"
              }
              return (
                <li key={item.value}>
                  <div className={wrapperClasses}>
                    <label className="flex items-start text-sm leading-5 space-x-3 cursor-pointer">
                      <input
                        name={id}
                        type="radio"
                        className="focus:ring-secondary-500 h-4 w-4 text-secondary-600 cursor-pointer mt-1 flex-shrink-0"
                        aria-describedby="plan-option-pricing-0 plan-option-limit-0"
                        onClick={() => input.onChange(item.value)}
                        disabled={submitting}
                        onChange={props.onChange}
                      />
                      <span className="flex-1">
                        {typeof item.label === "string" ? (
                          <span className="font-medium text-gray-900">{item.label}</span>
                        ) : (
                          item.label
                        )}
                      </span>
                    </label>
                  </div>
                </li>
              )
            })}
          </ul>
        </fieldset>
        <div role="alert" className="text-red-700">
          {showError && normalizedError}
        </div>
      </div>
    )
  }
)

export default CompactRadioGroupField
